import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import { userAPI } from '../../services/apiService';

interface ProfileImageUploadPageProps {
  onNavigate: (page: string) => void;
}

// Validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// Validate file (moved outside component since it doesn't depend on props/state)
const validateFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Invalid file type. Only JPG, PNG, and WebP are allowed.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'File size exceeds 5MB limit.';
  }
  return null;
};

const ProfileImageUploadPage: React.FC<ProfileImageUploadPageProps> = ({ onNavigate }) => {
  const { user, token } = useAuth();
  const navigation = useNavigation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    setValidationError(null);

    const validationErrorMsg = validateFile(file);
    if (validationErrorMsg) {
      setValidationError(validationErrorMsg);
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle file picker click
  const handleFilePickerClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !user?.id || !token) {
      setError('Please select an image file.');
      return;
    }

    const validationErrorMsg = validateFile(selectedFile);
    if (validationErrorMsg) {
      setValidationError(validationErrorMsg);
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setValidationError(null);

      const response = await userAPI.uploadProfileImage(user.id, selectedFile, token);

      if (response.success) {
        // Navigate back to profile page
        try {
          navigation.navigate('/profile');
        } catch {
          onNavigate('profile');
        }
      } else {
        setError(response.message || 'Failed to upload image');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    try {
      navigation.navigate('/profile');
    } catch {
      onNavigate('profile');
    }
  };

  // Handle back button
  const handleBack = () => {
    handleCancel();
  };

  return (
    <div className="min-h-screen bg-paper relative overflow-hidden">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-coffee">Upload Profile Photo</h1>
            <button
              onClick={handleBack}
              className="bg-white border-2 border-coffee text-coffee rounded-xl px-4 py-2 font-bold shadow-sm hover:bg-cream transition-colors"
            >
              ← Back
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Validation Error Display */}
          {validationError && (
            <div className="mb-6 p-4 bg-yellow-100 border-2 border-yellow-400 rounded-lg">
              <p className="text-yellow-700 text-sm">{validationError}</p>
            </div>
          )}

          {/* Main Container */}
          <div className="bg-cream rounded-[32px] border-4 border-coffee shadow-[8px_8px_0px_#594A42] p-8 md:p-12 relative z-10 mb-8">
            
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-4 border-dashed rounded-xl p-12 text-center mb-6 transition-colors ${
                previewUrl
                  ? 'border-coffee/30 bg-cream'
                  : 'border-coffee/50 bg-white hover:border-coffee hover:bg-cream/50'
              }`}
            >
              {previewUrl ? (
                <div className="space-y-4">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-64 h-64 rounded-full object-cover border-4 border-white ring-2 ring-coffee shadow-md mx-auto"
                  />
                  <p className="text-coffee/70 text-sm">
                    {selectedFile?.name} ({(selectedFile?.size ? selectedFile.size / 1024 / 1024 : 0).toFixed(2)} MB)
                  </p>
                  <button
                    onClick={handleFilePickerClick}
                    className="text-terracotta hover:text-[#d89050] text-sm underline"
                  >
                    Choose a different image
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-6xl mb-4">📸</div>
                  <h3 className="text-xl font-bold text-coffee">Drag & Drop Your Photo</h3>
                  <p className="text-coffee/70 mb-4">or</p>
                  <button
                    onClick={handleFilePickerClick}
                    className="bg-terracotta text-white border-2 border-coffee rounded-xl px-6 py-3 font-semibold hover:bg-[#d89050] transition-colors shadow-sm"
                  >
                    Browse Files
                  </button>
                  <p className="text-coffee/70 text-xs mt-4">
                    Recommended: 400x400px • Max 5MB • JPG, PNG, WebP
                  </p>
                </div>
              )}
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileInputChange}
              className="hidden"
            />

            {/* Upload Progress Indicator */}
            {uploading && (
              <div className="mb-6 p-4 bg-blue-100 border-2 border-blue-400 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <p className="text-blue-700 text-sm">Uploading your photo...</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handleCancel}
                disabled={uploading}
                className="flex-1 bg-white border-2 border-coffee text-coffee rounded-xl py-4 font-bold text-lg shadow-[4px_4px_0px_#594A42] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#594A42] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="flex-1 bg-terracotta text-white border-2 border-coffee rounded-xl py-4 font-bold text-lg shadow-[4px_4px_0px_#594A42] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#594A42] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Save Photo'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileImageUploadPage;
